import { create } from 'zustand';
import { domainAPI } from './api/domain';
import { IDNSRecord, ICommonDomain, ICommonDomainCheckResponse, IEditCommonDomainRequest, IEditNameserverRequest, IDNSSEC } from '@cuspar/ywt.ch.model';

type domainStore = {
  checkDomainAvailability: (domain: string) => Promise<ICommonDomainCheckResponse[]>;
  domains: ICommonDomain[];
  domain?: ICommonDomain;
  getMyDomains: () => Promise<void>;
  getDomain: (domain: string) => Promise<void>;
  editDomain: (domain: string, body: IEditCommonDomainRequest) => Promise<void>;
  listDNSRecords: (domain: string) => Promise<void>;
  upsertDNSRecord: (domain: string, body: IDNSRecord) => Promise<void>;
  deleteDNSRecord: (domain: string, body: IDNSRecord) => Promise<void>;
  changeNameserver: (domain: string, nameserver: IEditNameserverRequest) => Promise<boolean>;
  syncDomainFromEppToDb: (domain: string) => Promise<void>;
  scanPublicDNS: (domain: string, hosts?: string[]) => Promise<IDNSRecord[]>;
  addDnssecRecord: (domain: string, body: IDNSSEC) => Promise<boolean>;
  removeDnssecRecord: (domain: string, body: IDNSSEC) => Promise<boolean>;
  removeDomain: (domain: string) => Promise<void>;
};

export const useDomainStore = create<domainStore>((set) => ({
  checkedDomains: [],
  checkDomainAvailability: async (domainName) => {
    const response = await domainAPI.checkDomainAvailability(domainName);
    return response.success ? response.data : [];
  },
  domains: [],
  getMyDomains: async () => {
    const response = await domainAPI.getMyDomains();
    set({ domains: response.success ? response.data : [] });
  },
  getDomain: async (domain: string) => {
    const response = await domainAPI.getDomain(domain);
    set({ domain: response.success ? response.data : undefined });
  },
  editDomain: async (domain: string, body: IEditCommonDomainRequest) => {
    const response = await domainAPI.editDomain(domain, body);
    if (!response.success) return;
    set((state) => ({
      ...state,
      domain: response.data,
    }));
  },
  listDNSRecords: async (domain: string) => {
    const response = await domainAPI.listDNSRecords(domain);
    if (!response.success) return;
    set((state) => {
      if (!state.domain) return state;
      return { ...state, domain: { ...state.domain, dnsRecords: response.data } };
    });
  },
  upsertDNSRecord: async (domain: string, body: IDNSRecord) => {
    const response = await domainAPI.upsertDNSRecord(domain, body);
    if (!response.success) return;
    // merge by id to support multiple records for same name+type (e.g., MX)
    set((state) => {
      if (!state.domain) return state;
      const created = response.data;
      const records = [...state.domain.dnsRecords];

      if (body.id) {
        // Try to replace the record that was edited (identified by previous id)
        const prevIdx = records.findIndex((r) => r.id === body.id);
        if (prevIdx !== -1) {
          records.splice(prevIdx, 1, created);
        } else {
          // If previous id not found, ensure we don't duplicate by new id
          const newIdx = records.findIndex((r) => r.id === created.id);
          if (newIdx !== -1) records.splice(newIdx, 1, created);
          else records.push(created);
        }
      } else {
        // Create flow: push unless the exact new id already exists
        const newIdx = records.findIndex((r) => r.id === created.id);
        if (newIdx !== -1) records.splice(newIdx, 1, created);
        else records.push(created);
      }

      return { ...state, domain: { ...state.domain, dnsRecords: records } };
    });
  },
  deleteDNSRecord: async (domain: string, body: IDNSRecord) => {
    const response = await domainAPI.deleteDNSRecord(domain, body);
    if (!response.success) return;
    set((state) => {
      if (!state.domain) return state;
      let next = state.domain.dnsRecords;
      if (body.id) {
        next = next.filter((r) => r.id !== body.id);
      } else {
        // Fallback matching when id is missing
        next = next.filter((r) => !(r.name === body.name && r.type === body.type && r.content === body.content && r.priority === body.priority));
      }
      return { ...state, domain: { ...state.domain, dnsRecords: [...next] } };
    });
  },
  changeNameserver: async (domain: string, nameserver: IEditNameserverRequest) => {
    const response = await domainAPI.changeNameserver(domain, nameserver);
    if (!response.success) return false;
    set((state) => ({
      ...state,
      domain: state.domain ? { ...state.domain, nameservers: response.data.nameservers, useYwtDns: response.data.useYwtDns } : undefined,
    }));
    return true;
  },
  syncDomainFromEppToDb: async (domain: string) => {
    const response = await domainAPI.syncDomainFromEppToDb(domain);
    if (!response.success) return;
    set((state) => ({
      ...state,
      domain: state.domain ? { ...state.domain, nameservers: response.data.nameservers } : undefined,
    }));
  },
  scanPublicDNS: async (domain: string, hosts?: string[]) => {
    const response = await domainAPI.scanDNSRecords(domain, hosts);
    return response.success ? response.data : [];
  },
  addDnssecRecord: async (domain: string, body: IDNSSEC) => {
    const response = await domainAPI.addDnssecRecord(domain, body);
    if (!response.success) return false;
    set((state) => ({ ...state, domain: response.data }));
    return true;
  },
  removeDnssecRecord: async (domain: string, body: IDNSSEC) => {
    const response = await domainAPI.removeDnssecRecord(domain, body);
    if (!response.success) return false;
    set((state) => ({ ...state, domain: response.data }));
    return true;
  },
  removeDomain: async (domain: string) => {
    const response = await domainAPI.removeDomain(domain);
    if (!response.success) return;
    set((state) => ({ ...state, domains: state.domains.filter((d) => d.domain !== domain) }));
  },
}));
